import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import {
  courtCasesApi,
  invoicesApi,
  expensesApi,
  clientsApi,
  tasksApi,
  appointmentsApi,
  extractList,
} from '../../api/facades';

type DateRangeKey = 'this_month' | 'last_month' | 'this_year' | 'all_time';

const formatMoney = (amount: number): string => {
  const value = Number(amount || 0);
  return `MWK ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getRange = (key: DateRangeKey) => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (key === 'this_month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (key === 'last_month') {
    start.setMonth(start.getMonth() - 1, 1);
    start.setHours(0, 0, 0, 0);
    end.setMonth(end.getMonth() - 1 + 1, 0); // last day of last month
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  if (key === 'this_year') {
    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  return { start: null as Date | null, end: null as Date | null };
};

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState<DateRangeKey>('this_month');
  const { start: rangeStart, end: rangeEnd } = getRange(rangeKey);

  const { data: casesRes } = useQuery({
    queryKey: ['court-cases'],
    queryFn: () => courtCasesApi.list({ perPage: 100 }),
  });
  const cases = extractList(casesRes);

  const { data: invoicesRes } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoicesApi.list({}),
  });
  const invoices = extractList(invoicesRes);

  const { data: expensesRes } = useQuery({
    queryKey: ['expenses'],
    queryFn: () => expensesApi.list({}),
  });
  const expenses = extractList(expensesRes);

  const { data: clientsRes } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list({}),
  });
  const clients = extractList(clientsRes);

  const { data: tasksRes } = useQuery({
    queryKey: ['tasks', 'reports'],
    queryFn: () => tasksApi.list({ per_page: 200 }),
  });
  const tasks = extractList(tasksRes);

  const { data: apptsRes } = useQuery({
    queryKey: ['appointments', 'reports'],
    queryFn: () =>
      appointmentsApi.list({
        dateFrom: new Date().toISOString().slice(0, 10),
      }),
  });
  const appointments = extractList(apptsRes);

  const isInRange = (value: any): boolean => {
    if (!rangeStart || !rangeEnd) return true;
    if (!value) return false;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    return d >= rangeStart && d <= rangeEnd;
  };

  const stats = useMemo(() => {
    const casesInRange = cases.filter((c: any) =>
      isInRange(c.filing_date || c.created_at),
    );
    const invoicesInRange = invoices.filter((inv: any) =>
      isInRange(inv.inv_date || inv.created_at),
    );
    const expensesInRange = expenses.filter((exp: any) =>
      isInRange(exp.exp_date || exp.created_at),
    );
    const clientsInRange = clients.filter((cl: any) =>
      isInRange(cl.created_at),
    );

    const openCases = casesInRange.filter(
      (c: any) => String(c.is_active ?? '').toLowerCase() === 'yes',
    );
    const closedCases = casesInRange.filter((c: any) => {
      const statusName = c.case_status?.case_status_name || c.status || '';
      return String(statusName).toLowerCase().includes('closed');
    });

    const totalRevenue = invoicesInRange.reduce(
      (sum: number, inv: any) =>
        sum + (parseFloat(inv.total_amount ?? inv.amount) || 0),
      0,
    );
    const collectedRevenue = invoicesInRange.reduce((sum: number, inv: any) => {
      const status = String(inv.inv_status || '').toLowerCase();
      if (!status.includes('paid')) return sum;
      return sum + (parseFloat(inv.total_amount ?? inv.amount) || 0);
    }, 0);

    const totalExpenses = expensesInRange.reduce(
      (sum: number, exp: any) =>
        sum + (parseFloat(exp.total_amount ?? exp.amount) || 0),
      0,
    );

    const casesByPriority = {
      High: casesInRange.filter((c: any) => c.priority === 'High').length,
      Medium: casesInRange.filter((c: any) => c.priority === 'Medium').length,
      Low: casesInRange.filter((c: any) => c.priority === 'Low').length,
    };

    const casesByStatus = ['Open', 'Pending', 'Closed'].reduce(
      (acc, label) => {
        const key = label.toLowerCase();
        acc[label] = casesInRange.filter((c: any) => {
          const statusName = c.case_status?.case_status_name || c.status || '';
          const s = String(statusName).toLowerCase();
          if (key === 'open') return !s.includes('closed');
          return s.includes(key);
        }).length;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      openCases: openCases.length,
      closedCases: closedCases.length,
      newClients: clientsInRange.length,
      totalRevenue,
      collectedRevenue,
      totalExpenses,
      net: collectedRevenue - totalExpenses,
      casesByPriority,
      casesByStatus,
    };
  }, [cases, clients, invoices, expenses, rangeStart, rangeEnd]);

  const topCasesColumns = [
    { key: 'case_number', label: 'Case No.', render: (r: any) => r.case_number ?? '—' },
    { key: 'party_name', label: 'Party' },
    { key: 'priority', label: 'Priority' },
    { key: 'filing_date', label: 'Filing Date', render: (r: any) => (r.filing_date ? String(r.filing_date).slice(0, 10) : '—') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          hideTitle
          subtitle="Matter, financial, and productivity insights for your practice."
        />
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Period:
          </label>
          <select
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value as DateRangeKey)}
            className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none"
          >
            <option value="this_month">This month</option>
            <option value="last_month">Last month</option>
            <option value="this_year">This year</option>
            <option value="all_time">All time</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Open Cases
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {stats.openCases}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {stats.closedCases} closed in period
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            New Clients
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {stats.newClients}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Clients created in selected period
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Billed vs Collected
          </div>
          <div className="mt-2 text-xl font-bold text-slate-700 dark:text-slate-200">
            {formatMoney(stats.totalRevenue)}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Collected: {formatMoney(stats.collectedRevenue)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Net Income
          </div>
          <div
            className={`mt-2 text-2xl font-bold ${
              stats.net >= 0 ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {formatMoney(stats.net)}
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            After expenses ({formatMoney(stats.totalExpenses)})
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">
            Case Pipeline
          </h2>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-sm text-slate-600 dark:text-slate-400">
                Open
              </dt>
              <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {stats.casesByStatus.Open}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-600 dark:text-slate-400">
                Pending
              </dt>
              <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {stats.casesByStatus.Pending}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-600 dark:text-slate-400">
                Closed
              </dt>
              <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {stats.casesByStatus.Closed}
              </dd>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  High priority
                </dt>
                <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {stats.casesByPriority.High}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  Medium priority
                </dt>
                <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {stats.casesByPriority.Medium}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-slate-600 dark:text-slate-400">
                  Low priority
                </dt>
                <dd className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {stats.casesByPriority.Low}
                </dd>
              </div>
            </div>
          </dl>
        </div>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">
            Financial Summary
          </h2>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-sm text-slate-600 dark:text-slate-400">
                Billed
              </dt>
              <dd className="text-sm font-semibold text-green-600">
                {formatMoney(stats.totalRevenue)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-slate-600 dark:text-slate-400">
                Collected
              </dt>
              <dd className="text-sm font-semibold text-emerald-600">
                {formatMoney(stats.collectedRevenue)}
              </dd>
            </div>
            <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
              <dt className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Net Income
              </dt>
              <dd
                className={`text-sm font-bold ${
                  stats.net >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatMoney(stats.net)}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
              Upcoming hearings & deadlines
            </h2>
            <button
              type="button"
              className="text-xs text-primary-600 hover:text-primary-800"
              onClick={() => navigate('/dashboard/calendar')}
            >
              Go to calendar
            </button>
          </div>
          <div className="space-y-3">
            {appointments.slice(0, 5).map((appt: any) => (
              <div
                key={`appt-${appt.id}`}
                className="flex items-center justify-between text-sm"
              >
                <div>
                  <div className="font-medium text-slate-700 dark:text-slate-200">
                    {appt.name || appt.type || 'Appointment'}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {appt.client?.name ?? 'Client'} —{' '}
                    {appt.date
                      ? String(appt.date).slice(0, 10)
                      : 'No date'}{' '}
                    {appt.time}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/appointments/${appt.id}`)}
                  className="text-xs text-primary-600 hover:text-primary-800"
                >
                  View
                </button>
              </div>
            ))}
            {appointments.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                No upcoming appointments from today.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
              Open tasks (by priority)
            </h2>
            <button
              type="button"
              className="text-xs text-primary-600 hover:text-primary-800"
              onClick={() => navigate('/dashboard/tasks')}
            >
              View tasks
            </button>
          </div>
          <ul className="space-y-2 text-sm">
            {['High', 'Medium', 'Low'].map((prio) => {
              const count = tasks.filter((t: any) => {
                const status = String(t.project_status || '').toLowerCase();
                if (
                  status.includes('closed') ||
                  status.includes('complete') ||
                  status.includes('completed')
                ) {
                  return false;
                }
                return t.priority === prio;
              }).length;
              return (
                <li
                  key={prio}
                  className="flex items-center justify-between text-slate-700 dark:text-slate-200"
                >
                  <span>{prio} priority</span>
                  <span className="font-semibold">{count}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
            High priority cases
          </h2>
          <Link to="/dashboard/court-cases">
            <span className="text-sm text-primary-600 hover:text-primary-800">
              View all cases
            </span>
          </Link>
        </div>
        <DataTable
          columns={topCasesColumns}
          data={cases
            .filter((c: any) => c.priority === 'High')
            .slice(0, 10)}
          loading={false}
          emptyMessage="No high priority cases."
          onRowClick={(id) => navigate(`/dashboard/court-cases/${id}`)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">
            Unpaid & overdue invoices
          </h2>
          <DataTable
            columns={[
              { key: 'invoice_no', label: 'Invoice' },
              {
                key: 'client',
                label: 'Client',
                render: (r: any) => r.client?.name ?? r.client_id ?? '—',
              },
              {
                key: 'total_amount',
                label: 'Amount',
                render: (r: any) => formatMoney(Number(r.total_amount ?? 0)),
              },
              {
                key: 'due_date',
                label: 'Due date',
                render: (r: any) =>
                  r.due_date ? String(r.due_date).slice(0, 10) : '—',
              },
            ]}
            data={invoices.filter((inv: any) => {
              const status = String(inv.inv_status || '').toLowerCase();
              return !status.includes('paid');
            })}
            loading={false}
            emptyMessage="All invoices are paid. Nice work."
            onRowClick={(id) => navigate(`/dashboard/invoices/${id}`)}
          />
        </div>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">
            Largest expenses (period)
          </h2>
          <DataTable
            columns={[
              { key: 'expense_name', label: 'Description' },
              {
                key: 'category',
                label: 'Category',
                render: (r: any) => r.category?.name ?? r.category ?? '—',
              },
              {
                key: 'total_amount',
                label: 'Amount',
                render: (r: any) => formatMoney(Number(r.total_amount ?? 0)),
              },
              {
                key: 'exp_date',
                label: 'Date',
                render: (r: any) =>
                  r.exp_date ? String(r.exp_date).slice(0, 10) : '—',
              },
            ]}
            data={expenses
              .filter((exp: any) =>
                isInRange(exp.exp_date || exp.created_at),
              )
              .sort(
                (a: any, b: any) =>
                  Number(b.total_amount ?? 0) - Number(a.total_amount ?? 0),
              )
              .slice(0, 10)}
            loading={false}
            emptyMessage="No expenses recorded for this period."
          />
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
